import React from 'react'
import {Box} from '@mui/material'

// Regime jurídico da análise. Define a base legal usada pela IA (persona/prompt),
// a base de conhecimento (Pinecone) e o conjunto de tipos de documento exibidos.
// '14133' = Lei Geral de Licitações (Administração Pública).
// '13303' = Lei das Estatais (empresas públicas e sociedades de economia mista).
export type Regime = '14133' | '13303'

export interface DocType {
    id: string
    apiId: string
    short: string
    name: string
    desc: string
    accent: string
    soft: string
    iconPath: string
    phase: string
    avgPages: string
    featured?: boolean
    // Ausente = '14133' (regime padrão). Tipos da Lei das Estatais marcam '13303'.
    regime?: Regime
}

export const DOC_TYPES: DocType[] = [
    {
        id: 'edital',
        apiId: '3514c57a-6ed3-4472-b4d5-2f1d9815400f',
        short: 'EDITAL',
        name: 'Edital (com ou sem anexos)',
        desc: 'Recomendado: envie o edital consolidado com seus anexos (TR, minuta de contrato, planilhas) em um único arquivo.',
        accent: '#050B29',
        soft: 'rgba(5,11,41,0.07)',
        iconPath: 'M3 4h18v4H3zM5 8v12h14V8M9 13h6M9 17h6',
        phase: 'Seleção',
        avgPages: '40-150 págs',
        featured: true,
    },
    {
        id: 'tr',
        apiId: '8b34584f-7781-4c91-8bd2-2f79c3889a6e',
        short: 'TR',
        name: 'Termo de Referência',
        desc: 'Especificação técnica detalhada do objeto da contratação.',
        accent: '#3acdb4',
        soft: 'rgba(58,205,180,0.12)',
        iconPath: 'M4 4h16v4H4zM4 10h10v10H4zM16 10h4v4h-4zM16 16h4v4h-4z',
        phase: 'Planejamento',
        avgPages: '20-60 págs',
    },
    {
        id: 'etp',
        apiId: 'c8eb8b29-758e-4898-9f10-e10c7d35e10f',
        short: 'ETP',
        name: 'Estudo Técnico Preliminar',
        desc: 'Documento que materializa o estudo técnico que fundamenta a contratação.',
        accent: '#3394EF',
        soft: 'rgba(51,148,239,0.10)',
        iconPath:
            'M9 3h6l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M14 3v5h5M8 13h8M8 17h5',
        phase: 'Planejamento',
        avgPages: '12-30 págs',
    },
    {
        id: 'contrato',
        apiId: 'b7799217-ba68-408f-931d-ab3b1f28b9aa',
        short: 'CONTRATO',
        name: 'Contrato Administrativo',
        desc: 'Instrumento que disciplina as obrigações entre Administração e contratado.',
        accent: '#B31417',
        soft: 'rgba(179,20,23,0.08)',
        iconPath:
            'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13l-4 4-2-2',
        phase: 'Execução',
        avgPages: '15-50 págs',
    },
    {
        id: 'mr',
        apiId: '2a2edaf0-c8c5-4b7a-8759-dcc487dc5921',
        short: 'MR',
        name: 'Mapa de Riscos',
        desc: 'Identificação, avaliação e tratamento de riscos da contratação.',
        accent: '#E0951B',
        soft: 'rgba(224,149,27,0.12)',
        iconPath: 'M12 3 2 21h20L12 3zM12 10v5M12 17.5h0.01',
        phase: 'Planejamento',
        avgPages: '5-15 págs',
    },
    {
        id: 'dfd',
        apiId: '3b86581f-cf66-40d1-96b3-950a9d709827',
        short: 'DFD',
        name: 'Documento de Formalização da Demanda',
        desc: 'Marco inicial do planejamento — registra a necessidade da contratação.',
        accent: '#7C5CFF',
        soft: 'rgba(124,92,255,0.10)',
        iconPath:
            'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h4',
        phase: 'Planejamento',
        avgPages: '2-6 págs',
    },
    {
        id: 'ata',
        apiId: 'b7287010-9bfe-419f-bb0c-b768cb1ac739',
        short: 'ATA',
        name: 'Ata de Registro de Preços',
        desc: 'Documento vinculativo, obrigacional, com características de compromisso para futura contratação.',
        accent: '#1F8D7A',
        soft: 'rgba(31,141,122,0.10)',
        iconPath:
            'M8 2h8a2 2 0 0 1 2 2v18l-6-3-6 3V4a2 2 0 0 1 2-2zM10 8h4M10 12h4',
        phase: 'Contratação',
        avgPages: '8-25 págs',
    },
    // ───────────────────────────────────────────────────────────────────
    // Lei das Estatais (Lei nº 13.303/2016). Os `apiId` (document_type_id)
    // serão preenchidos quando as minutas-gabarito das estatais forem
    // cadastradas como static_metrics no backend. Enquanto o apiId estiver
    // vazio, o tipo fica oculto (filtrado por `visibleDocumentTypes`).
    // ───────────────────────────────────────────────────────────────────
    {
        id: 'edital-estatal',
        // document_type_id reservado para o Edital da Lei das Estatais. O tipo só
        // aparece depois que o static_metric com este id for cadastrado no backend
        // (ver docs/regime-lei-estatais.md). Até lá, fica oculto.
        apiId: '4667f060-4c7c-4310-b2c1-43ab3f1ffad8',
        short: 'EDITAL',
        name: 'Edital (com ou sem anexos)',
        desc: 'Recomendado: envie o edital consolidado com seus anexos (TR, minuta de contrato, planilhas) em um único arquivo.',
        accent: '#1F8D7A',
        soft: 'rgba(31,141,122,0.10)',
        iconPath: 'M3 4h18v4H3zM5 8v12h14V8M9 13h6M9 17h6',
        phase: 'Seleção',
        avgPages: '40-150 págs',
        featured: true,
        regime: '13303',
    },
    {
        id: 'tr-estatal',
        // document_type_id reservado para o TR da Lei das Estatais (métrica de
        // análise, não gabarito). Oculto até o static_metric ser cadastrado.
        apiId: 'c19736c7-4f83-45f5-baab-694818836774',
        short: 'TR',
        name: 'Termo de Referência',
        desc: 'Especificação técnica detalhada do objeto da contratação.',
        accent: '#3acdb4',
        soft: 'rgba(58,205,180,0.12)',
        iconPath: 'M4 4h16v4H4zM4 10h10v10H4zM16 10h4v4h-4zM16 16h4v4h-4z',
        phase: 'Planejamento',
        avgPages: '20-60 págs',
        regime: '13303',
    },
    {
        id: 'etp-estatal',
        // document_type_id reservado para o ETP da Lei das Estatais (métrica de
        // análise, não gabarito). Oculto até o static_metric ser cadastrado.
        apiId: 'c5b3be2b-6c1d-4baf-b741-29bc29377cbd',
        short: 'ETP',
        name: 'Estudo Técnico Preliminar',
        desc: 'Documento que materializa o estudo técnico que fundamenta a contratação.',
        accent: '#3394EF',
        soft: 'rgba(51,148,239,0.10)',
        iconPath:
            'M9 3h6l5 5v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M14 3v5h5M8 13h8M8 17h5',
        phase: 'Planejamento',
        avgPages: '12-30 págs',
        regime: '13303',
    },
    {
        id: 'contrato-estatal',
        // document_type_id reservado para o Contrato da Lei das Estatais (métrica
        // de análise, não gabarito). Oculto até o static_metric ser cadastrado.
        apiId: '0aeb1f6b-b27d-4782-bde1-6c469ff8c0e6',
        short: 'CONTRATO',
        name: 'Contrato',
        desc: 'Instrumento que disciplina as obrigações entre a estatal e o contratado.',
        accent: '#B31417',
        soft: 'rgba(179,20,23,0.08)',
        iconPath:
            'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M16 13l-4 4-2-2',
        phase: 'Execução',
        avgPages: '15-50 págs',
        regime: '13303',
    },
]

export const findDocTypeByApiId = (apiId?: string | null): DocType | null =>
    (apiId && DOC_TYPES.find((docType) => docType.apiId === apiId)) || null

type DocumentTypeBadgeProps = {
    docType: DocType
    sx?: Record<string, unknown>
}

export function DocumentTypeBadge({docType, sx}: DocumentTypeBadgeProps) {
    return (
        <Box
            component="span"
            sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 10px',
                borderRadius: '999px',
                border: `1px solid ${docType.accent}`,
                backgroundColor: docType.soft,
                color: docType.accent,
                fontSize: '12px',
                fontWeight: 700,
                lineHeight: 1,
                whiteSpace: 'nowrap',
                flexShrink: 0,
                ...sx,
            }}
        >
            {docType.short}
        </Box>
    )
}
